import { Resolver } from 'apollo-client';
import { Analytics } from '@trello/atlassian-analytics';
import { featureFlagClient } from '@trello/feature-flag-client';

/**
 * Wrapper for resolver that will add analytics tracking to it
 */
export const addAnalytics = (
  resolverName: string,
  originalResolver: (...args: unknown[]) => unknown,
): Resolver => {
  const trackedResolver: Resolver = async (...args) => {
    // tracking every time when we hit resolver, but not take data from cache
    Analytics.sendOperationalEvent({
      source: '@trello/graphql',
      action: 'missed',
      actionSubject: 'apolloCache',
      attributes: {
        resolver: resolverName,
      },
    });

    // calling original resolver
    return originalResolver.apply(this, args);
  };
  return trackedResolver;
};

/**
 * Adding analytics to every resolver in the plain resolver map
 * @param obj resolver map object
 * @param type type name to prefix the operational event attribute with
 */
export const trackQueryResolvers = (
  obj: {
    [key: string]: (...args: unknown[]) => unknown;
  },
  type?: string,
) => {
  const sendingEnabled = featureFlagClient.get(
    'fep.send_apollo_cache_misses_to_gas',
    false,
  );

  return !sendingEnabled
    ? obj
    : Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [
          k,
          addAnalytics(type ? `${type}.${k}` : k, v),
        ]),
      );
};

/**
 * Adding analytics to every resolver within type resolver object
 * @param obj map of type resolvers
 */
export const trackTypeResolvers = (obj: {
  [key: string]: {
    [key: string]: (...args: unknown[]) => unknown;
  };
}) => {
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, trackQueryResolvers(v, k)]),
  );
};
